/* eslint-disable no-useless-catch */
import {apiClient} from './apiClient';
import {Test, Patient, Recommendation, UserInfo} from '@/shared/types/api';

export interface UserCoupon {
    id: number;
    code: string;
    description: string | null;
    discountType: 'percentage' | 'dollars';
    discountAmount: number;
    valid: boolean;
    expiresAt: string | null;
    usedOn: string | null;
    pointsCost: number;
}

export interface CouponOffer {
    discountType: 'percentage' | 'dollars';
    discountAmount: number;
    pointsCost: number;
    description: string | null;
    available: number;
}

export interface PointsLedgerRow {
    id: number;
    userId: number;
    points: number;
    balanceAfter: number;
    eventType: 'login' | 'purchase' | 'health_improvement';
    sourceType: string | null;
    sourceId: number | null;
    description: string | null;
    createdAt: string;
}

/**
 * User data service for handling user-related API calls
 * Following the service layer pattern from best practices guide
 */
export class UserService {
    /**
     * Get all user data including tests, results, and recommendations
     * This is the main endpoint that returns comprehensive user data
     */
    static async getUserData(): Promise<{orders: Test[]; categoryScores: Record<string, number>}> {
        try {
            return await apiClient.get<{orders: Test[]; categoryScores: Record<string, number>}>('/api/user/data');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get specific test by ID
     */
    static async getTest(testId: number): Promise<Test> {
        try {
            return await apiClient.get<Test>(`/api/user/tests/${testId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get tests for a specific patient
     */
    static async getTestsForPatient(patientId: number): Promise<Test[]> {
        try {
            return await apiClient.get<Test[]>(`/api/user/patients/${patientId}/tests`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get user's sub-patients (family members, etc.)
     */
    static async getSubPatients(): Promise<Patient[]> {
        try {
            return await apiClient.get<Patient[]>('/api/user/patients');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get full user profile by patient ID (for patient switching/impersonation)
     */
    static async getUserProfile(patientId: number): Promise<UserInfo> {
        try {
            return await apiClient.get<UserInfo>(`/api/user/profile/${patientId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Add a new sub-patient
     */
    static async addSubPatient(patientData: Omit<Patient, 'id'>): Promise<Patient> {
        try {
            return await apiClient.post<Patient>('/api/user/patients', patientData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update sub-patient information
     */
    static async updateSubPatient(patientId: number, updates: Partial<Patient>): Promise<Patient> {
        try {
            return await apiClient.patch<Patient>(`/api/user/patients/${patientId}`, updates);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete sub-patient
     */
    static async deleteSubPatient(patientId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/user/patients/${patientId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get available test panels
     */
    static async getPanels(): Promise<Array<{name: string; code: string}>> {
        try {
            return await apiClient.get<Array<{name: string; code: string}>>('/api/kit/getPanels');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get health recommendations
     */
    static async getRecommendations(): Promise<Recommendation[]> {
        try {
            return await apiClient.get<Recommendation[]>('/api/recommendations');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get recommendations for specific test results
     */
    static async getRecommendationsForTest(testId: number): Promise<Recommendation[]> {
        try {
            return await apiClient.get<Recommendation[]>(`/api/tests/${testId}/recommendations`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Mark recommendation as read/dismissed
     */
    static async dismissRecommendation(recommendationId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(`/api/recommendations/${recommendationId}/dismiss`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Download test results as PDF
     */
    static async downloadTestResults(testId: number): Promise<Blob> {
        try {
            const response = await apiClient.get(`/api/user/tests/${testId}/download`, {
                responseType: 'blob',
            });
            return response as Blob;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Share test results via email
     */
    static async shareTestResults(testId: number, email: string, message?: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(`/api/user/tests/${testId}/share`, {
                email,
                message,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get user's appointment history
     */
    static async getAppointments(): Promise<any[]> {
        try {
            return await apiClient.get<any[]>('/api/user/appointments');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Schedule new appointment
     */
    static async scheduleAppointment(appointmentData: any): Promise<any> {
        try {
            return await apiClient.post<any>('/api/user/appointments', appointmentData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cancel appointment
     */
    static async cancelAppointment(appointmentId: number): Promise<{success: boolean}> {
        try {
            return await apiClient.delete<{success: boolean}>(`/api/user/appointments/${appointmentId}`);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update user preferences/settings
     */
    static async updatePreferences(preferences: Record<string, any>): Promise<{success: boolean}> {
        try {
            return await apiClient.patch<{success: boolean}>('/api/user/preferences', preferences);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get user preferences/settings
     */
    static async getPreferences(): Promise<Record<string, any>> {
        try {
            return await apiClient.get<Record<string, any>>('/api/user/preferences');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Upload profile photo
     */
    static async uploadProfilePhoto(file: File, onProgress?: (progress: number) => void): Promise<{photoUrl: string}> {
        try {
            return await apiClient.uploadFile<{photoUrl: string}>('/api/user/profile-photo', file, onProgress);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete user account
     */
    static async deleteAccount(password: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/user/delete-account', {password});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Export all user data
     */
    static async exportUserData(): Promise<Blob> {
        try {
            return await apiClient.get('/api/user/export', {
                responseType: 'blob',
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update user profile information
     */
    static async updateUser(userData: any): Promise<any> {
        try {
            return await apiClient.post<any>('/api/user', userData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Verify user's current password
     */
    static async verifyPassword(email: string, password: string): Promise<any> {
        try {
            return await apiClient.post<any>('/api/verifyPassword', {
                username: email,
                password: password,
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Change user password
     */
    static async changePassword(email: string, newPassword: string): Promise<any> {
        try {
            return await apiClient.post<any>('/api/admin/reset', {
                username: email,
                password: newPassword,
                ref: 'admin',
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get current points balance
     */
    static async getPoints(): Promise<{totalPoints: number; lifetimePoints: number}> {
        try {
            return await apiClient.get<{totalPoints: number; lifetimePoints: number}>('/api/user/points');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get points earning history
     */
    static async getPointsHistory(limit = 50): Promise<{history: PointsLedgerRow[]}> {
        try {
            return await apiClient.get<{history: PointsLedgerRow[]}>(`/api/user/points/history?limit=${limit}`);
        } catch (error) {
            throw error;
        }
    }

    static async getCoupons(): Promise<{coupons: UserCoupon[]}> {
        try {
            return await apiClient.get<{coupons: UserCoupon[]}>('/api/user/coupons');
        } catch (error) {
            throw error;
        }
    }

    static async getAvailableCoupons(): Promise<{offers: CouponOffer[]}> {
        try {
            return await apiClient.get<{offers: CouponOffer[]}>('/api/user/coupons/available');
        } catch (error) {
            throw error;
        }
    }

    static async claimCoupon(offer: Pick<CouponOffer, 'discountType' | 'discountAmount' | 'pointsCost'>): Promise<{code: string; couponId: number}> {
        try {
            return await apiClient.post<{code: string; couponId: number}>('/api/user/coupons/claim', offer);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Refresh access token using refresh token
     */
    static async refreshToken(refreshToken: string): Promise<{accessToken: string; refreshToken: string}> {
        try {
            return await apiClient.post<{accessToken: string; refreshToken: string}>('/api/auth/refresh', {
                refreshToken,
            });
        } catch (error) {
            throw error;
        }
    }
}

export default UserService;
